import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface EntrySubmittedModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Reservation id returned by POST /pickems/:id/reserve, shown as "Confirmation: <uuid>". */
  reservationId: string | null;
  onGoToUpcoming: () => void;
  onBackToLobby: () => void;
}

/**
 * "Entry Submitted" modal — DKPLAT-528. Shown after a successful reservation
 * POST. Reuses lucide-react's CheckCircle (already the icon library used
 * throughout the app) rather than a new icon asset.
 */
const EntrySubmittedModal: React.FC<EntrySubmittedModalProps> = ({
  isOpen,
  onClose,
  reservationId,
  onGoToUpcoming,
  onBackToLobby,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        data-test-id="entry-submitted-modal"
        className="bg-app-background border-border/30 w-[calc(100%-2rem)] max-w-md mx-auto rounded-xl p-6"
      >
        <div className="text-center space-y-6 pt-2">
          <h2 className="text-text-primary font-bold text-xl tracking-wide">Entry Submitted</h2>

          <div className="flex justify-center">
            <CheckCircle className="w-14 h-14 text-pickem-primary" strokeWidth={1.5} />
          </div>

          <p className="text-text-secondary text-sm leading-relaxed">
            You're in! Don't forget to come back and make your picks!
          </p>

          {reservationId && (
            <p
              data-test-id="entry-submitted-confirmation-id"
              className="text-text-primary text-xs font-bold break-all"
            >
              Confirmation: {reservationId}
            </p>
          )}

          <div className="space-y-3">
            <Button
              data-test-id="entry-submitted-go-to-upcoming-btn"
              variant="pickem"
              onClick={onGoToUpcoming}
              className="w-full"
            >
              Go To Upcoming
            </Button>
            <Button
              data-test-id="entry-submitted-back-to-lobby-btn"
              variant="outline"
              onClick={onBackToLobby}
              className="w-full bg-transparent border-pickem-primary text-pickem-primary hover:bg-pickem-primary/10"
            >
              Back To Lobby
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EntrySubmittedModal;
